/**
 * Deterministic kinematic character controller, shared verbatim by the
 * client (prediction) and the server (authority) so reconciliation is exact.
 * Pure JS doubles + axis-separated AABB collide-and-slide against MapDef
 * boxes — no physics engine on this path by design: identical code on both
 * sides beats approximately-matching engines. (Rapier remains the upgrade
 * path if maps ever need non-box collision.)
 */
import { Buttons } from "./protocol.js";
import { yawPitchToDir, rayAabb } from "./raycast.js";
// --- tunables (config-driven; never hardcode elsewhere) --------------------
export const MOVE = {
    walkSpeed: 5.7,
    sprintSpeed: 9.2,
    crouchSpeed: 3.0,
    adsSpeedMult: 0.62,
    groundAccel: 11, // 1/s — exponential approach rate to wish velocity
    airAccel: 2.2,
    antigravSpeedMult: 2.4,
    // slide: crouch while sprinting → a momentum burst that bleeds off slowly
    slideSpeed: 11.4, // launch speed
    slideFriction: 3.2, // low accel-rate while sliding = momentum carries
    slideMinSpeed: 4.2, // below this (≈crouchSpeed*1.4) the slide ends
    // bounce/jump pads: stepping on one flings you skyward + forward (fling)
    jumpPad: { power: 14.5, radius: 1.5, height: 1.6, fling: 9 },
    // RUBBER modifier (opt-in): impact speed past this (m/s downward) rebounds off
    // the floor at the caller's restitution. Above a hop (jumpVel 8.2) so a stand
    // does not fizz, but any real fall/jump-landing trampolines. See modBounce.
    bounceMinSpeed: 4.5,
    // jetpack elytra glide: look-direction flight, gain speed diving, lose it climbing
    // sprintMult = afterburner: holding Sprint (Shift / L3) raises target + cap
    glide: { gravity: 7, forward: 16, dive: 26, climbDrag: 1.2, maxSpeed: 30, boost: 8, sprintMult: 1.45 },
    // SWIMMING: full 3D control in water volumes (tag "water"). Jump = kick up,
    // Crouch = dive, forward follows your look pitch; heavy drag = water inertia.
    swim: {
        speed: 4.8, // horizontal swim pace
        up: 5.2, // Jump kick toward the surface
        down: 4.4, // Crouch dive
        lookVert: 3.6, // forward-in-look-direction vertical component
        buoyancy: 1.6, // idle upward drift (float toward the surface)
        drag: 3.4, // 1/s velocity damping (water resistance)
        breach: 5.6, // upward speed granted to leap OUT at the surface (Jump)
    },
    // ZIPLINES: touch a cable in mid-air to catch it, ride to the lower end,
    // press Jump to let go with momentum. No button to attach — you grab it by
    // jumping into it.
    zip: {
        speed: 18, // ride speed along the cable (m/s)
        grab: 1.5, // how close you must pass to catch a cable (m)
        hang: 1.15, // how far below the cable the player hangs (m)
        cooldown: 0.45, // seconds before you can grab again after letting go
        exit: 1.2, // launch speed multiplier when you jump off
    },
    // GRAPPLING HOOK: hold Grapple to latch onto whatever you're looking at and
    // reel in. Purely additive — inert unless the button is held.
    grapple: {
        range: 44, // max line length (m)
        pull: 27, // reel-in speed (m/s)
        accel: 7, // how fast velocity converges on the pull (1/s)
        arrive: 2.6, // detach once this close to the anchor
        minDist: 3, // ignore anchors closer than this (no point-blank yank)
    },
    gravity: 24,
    jumpVel: 8.2,
    stepHeight: 0.55,
    playerHalfWidth: 0.4,
    standHeight: 1.7,
    crouchHeight: 1.2,
    standEye: 1.58,
    crouchEye: 1.05,
    /** Server clamps client dt to this range (ms) — anti-speedhack. */
    minDtMs: 1,
    maxDtMs: 50,
};
export function spawnKinematics(p) {
    return { x: p[0], y: p[1], z: p[2], vx: 0, vy: 0, vz: 0, onGround: false, crouching: false, inWater: false, submerged: false, grapOn: false };
}
/**
 * Append one moving-platform collider to `dynamic`, REUSING a pooled object
 * instead of allocating a fresh literal.
 *
 * The dynamic collider set is torn down and rebuilt every tick (server) and
 * every frame (client) on train / cable / elevator / mover / UFO maps — Skyhaven
 * alone rebuilds ~8 of these 30–240×/s, the practice field 35×. A `{ ... }`
 * literal per box was steady heap garbage on exactly the sim/render threads
 * whose GC pauses spike the server tick (tripping the 20 Hz throttle every
 * client feels) and stutter the client frame.
 *
 * `pool[dynamic.length]` is the trick: callers reset `dynamic.length = 0` once
 * per frame (see `beginDynamic`) and thereafter push ONLY pooled boxes, so
 * `dynamic.length` at each push is a monotonic 0,1,2,… index into the pool. The
 * pool grows to the high-water box count once and never allocates again; the
 * per-frame `dynamic.length = 0` merely drops references — the pooled objects
 * live on the world. Every field (including the optional rotation ones) is
 * assigned on every push, so a reused slot never carries stale UFO carry data.
 */
export function pushDyn(dynamic, pool, minX, maxX, minY, maxY, minZ, maxZ, dx, dy, dz, rotCx, rotCz, rotCx2, rotCz2, dyaw) {
    const i = dynamic.length;
    let b = pool[i];
    if (!b) {
        b = { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0, dx: 0, dy: 0, dz: 0 };
        pool[i] = b;
    }
    b.minX = minX;
    b.maxX = maxX;
    b.minY = minY;
    b.maxY = maxY;
    b.minZ = minZ;
    b.maxZ = maxZ;
    b.dx = dx;
    b.dy = dy;
    b.dz = dz;
    b.rotCx = rotCx;
    b.rotCz = rotCz;
    b.rotCx2 = rotCx2;
    b.rotCz2 = rotCz2;
    b.dyaw = dyaw;
    dynamic.push(b);
}
export function boxToAabb(b) {
    return {
        minX: b.p[0] - b.s[0] / 2, maxX: b.p[0] + b.s[0] / 2,
        minY: b.p[1] - b.s[1] / 2, maxY: b.p[1] + b.s[1] / 2,
        minZ: b.p[2] - b.s[2] / 2, maxZ: b.p[2] + b.s[2] / 2,
    };
}
/**
 * Uniform X/Z grid over the static colliders, so a collision or ray query tests
 * a handful of nearby boxes instead of the whole map.
 *
 * WHY: `collides()` and `lineOfSight()` both scanned `world.aabbs` linearly.
 * Collider counts run 19 (plumbing_plaza) to 962 (medieval_castle), and
 * `agent/bench-tick-live.mjs` measures the consequence on a real gameserver —
 * holding bots fixed at 8 and changing ONLY the map, the tick goes 2.0 ms
 * (grid_arena, 36 colliders) → 4.6 ms (medieval_castle, 962). That 2.6 ms is
 * the term this index removes.
 *
 * CONSERVATIVE BY CONSTRUCTION (invariant 7 — shared movement is deterministic
 * and client prediction must agree bit-exactly with the server). A box is
 * registered in EVERY cell its X/Z footprint touches, and a query visits every
 * cell the query volume touches, so the candidate set is always a SUPERSET of
 * what the linear scan would have tested. Both consumers reduce their candidates
 * with a boolean any-hit, so a superset — including the duplicates a box
 * spanning several cells produces — yields an identical answer. This is why the
 * index is safe to use on the prediction path at all: it changes which boxes get
 * skipped, never which boxes can match.
 *
 * Indexing is X/Z only. Maps are far wider than they are tall, so a third axis
 * buys little; Y is still discriminated exactly by the caller's final test.
 */
class ColliderGrid {
    cells;
    nx;
    nz;
    minX;
    minZ;
    inv;
    /** Boxes whose footprint is so large that cell-listing them is pointless —
     *  ground slabs span the whole map and would land in every cell. Kept in one
     *  always-tested list instead, which is cheaper than bloating every cell. */
    huge;
    /** Guards against a mutation that forgot to call `invalidateColliderIndex`.
     *  Every current mutation site (doors, drawbridges) changes the length. */
    builtLength;
    /**
     * True when the map is too small to index and every box sits in `huge`.
     *
     * Callers should then run their ORIGINAL inline scan rather than calling
     * `anyInAabb`/`anyOnSegment`. Routing through the index would still be
     * correct, but it pays a per-box callback indirection that the inline loop
     * does not — measured at 0.70-0.89x (i.e. a net slowdown) on the small maps,
     * where there is no scan cost to amortise it against.
     */
    passthrough;
    /** Monotonic per-query counter for `anyOnSegment`'s visited-stamp dedup.
     *  Bumped once per DDA call; a box whose `_qseg` already equals it this query
     *  is skipped. Never wraps in any realistic run (2^53 queries). */
    qseg = 0;
    constructor(boxes, cell) {
        this.builtLength = boxes.length;
        let mnX = Infinity, mnZ = Infinity, mxX = -Infinity, mxZ = -Infinity;
        for (const b of boxes) {
            if (b.minX < mnX)
                mnX = b.minX;
            if (b.minZ < mnZ)
                mnZ = b.minZ;
            if (b.maxX > mxX)
                mxX = b.maxX;
            if (b.maxZ > mxZ)
                mxZ = b.maxZ;
            b._qseg = 0; // give every box the visited-stamp field up front (one shape)
        }
        if (!Number.isFinite(mnX)) {
            mnX = 0;
            mnZ = 0;
            mxX = 0;
            mxZ = 0;
        }
        this.minX = mnX;
        this.minZ = mnZ;
        this.inv = 1 / cell;
        // Cap the grid so a pathological map cannot allocate a huge table.
        this.nx = Math.max(1, Math.min(160, Math.ceil((mxX - mnX) * this.inv) + 1));
        this.nz = Math.max(1, Math.min(160, Math.ceil((mxZ - mnZ) * this.inv) + 1));
        this.cells = new Array(this.nx * this.nz);
        for (let i = 0; i < this.cells.length; i++)
            this.cells[i] = [];
        this.huge = [];
        // SMALL MAPS SKIP THE INDEX. Everything lands in `huge` (which every query
        // scans unconditionally), so the grid degenerates to the original linear
        // scan. Measured: at 19-21 colliders the DDA/cell-walk bookkeeping costs
        // MORE than just testing every box (agent/bench-los.mjs showed 0.70x and
        // 0.81x — i.e. a slowdown — on plumbing_plaza and chibre_arena before this
        // branch existed). The crossover sits well under 64, so this is deliberately
        // conservative: below it, pay nothing and behave exactly as before.
        if (boxes.length < 64) {
            for (const b of boxes)
                this.huge.push(b);
            this.passthrough = true;
            return;
        }
        this.passthrough = false;
        // A box wider than this many cells goes in `huge` rather than being listed
        // in each of them — the crossover where listing costs more than testing.
        const spanCap = 8;
        for (const b of boxes) {
            const x0 = this.cx(b.minX), x1 = this.cx(b.maxX);
            const z0 = this.cz(b.minZ), z1 = this.cz(b.maxZ);
            if ((x1 - x0 + 1) * (z1 - z0 + 1) > spanCap * spanCap) {
                this.huge.push(b);
                continue;
            }
            for (let z = z0; z <= z1; z++) {
                for (let x = x0; x <= x1; x++)
                    this.cells[z * this.nx + x].push(b);
            }
        }
    }
    // Both clamps are written so NaN falls through to 0 rather than propagating.
    // `i < 0 ? 0 : i >= n ? n-1 : i` returns NaN for a NaN input, which indexes
    // `cells` out of bounds and throws — turning what used to be a harmless wrong
    // boolean from the linear scan into a crash inside the room tick. Testing the
    // in-range case POSITIVELY (`i >= 0`) sends NaN to the 0 branch instead.
    cx(v) {
        const i = Math.floor((v - this.minX) * this.inv);
        return i >= 0 ? (i < this.nx ? i : this.nx - 1) : 0;
    }
    cz(v) {
        const i = Math.floor((v - this.minZ) * this.inv);
        return i >= 0 ? (i < this.nz ? i : this.nz - 1) : 0;
    }
    /** True if any box overlapping the query volume satisfies `hit`. */
    anyInAabb(minX, minZ, maxX, maxZ, hit) {
        for (const b of this.huge)
            if (hit(b))
                return true;
        const x0 = this.cx(minX), x1 = this.cx(maxX);
        const z0 = this.cz(minZ), z1 = this.cz(maxZ);
        for (let z = z0; z <= z1; z++) {
            const row = z * this.nx;
            for (let x = x0; x <= x1; x++) {
                const list = this.cells[row + x];
                for (let i = 0; i < list.length; i++)
                    if (hit(list[i]))
                        return true;
            }
        }
        return false;
    }
    /**
     * True if any box along the X/Z projection of the segment satisfies `hit`.
     *
     * Walks cells with a 2D DDA rather than testing the segment's bounding box:
     * a long diagonal sight line's bounding box covers a large fraction of the
     * map, which would give back most of the win. If the segment intersects a box
     * in 3D then its X/Z projection intersects that box's footprint, so the DDA
     * necessarily enters a cell holding it — the superset property still holds.
     */
    anyOnSegment(ox, oz, tx, tz, hit) {
        for (const b of this.huge)
            if (hit(b))
                return true;
        // Visited-stamp dedup: a box spanning several cells is listed in each, so a
        // DDA crossing >1 of them would call `hit` on it repeatedly (dup factor
        // 1.4-2.0x measured across the maps). `hit` is a pure function of the box
        // and the fixed ray, so a re-test can only reproduce the first result —
        // skipping it is bit-identical (both the any-hit `hit` and nearestStaticHit's
        // running-min are idempotent on a re-test). `huge` boxes are scanned once
        // above and never listed in cells, so they need no stamp.
        const qg = ++this.qseg;
        let x = this.cx(ox), z = this.cz(oz);
        const ex = this.cx(tx), ez = this.cz(tz);
        const dx = tx - ox, dz = tz - oz;
        const stepX = dx > 0 ? 1 : -1, stepZ = dz > 0 ? 1 : -1;
        // Distance (in t, where the segment is t=0..1) to the next cell boundary.
        const tDeltaX = dx !== 0 ? Math.abs(1 / (dx * this.inv)) : Infinity;
        const tDeltaZ = dz !== 0 ? Math.abs(1 / (dz * this.inv)) : Infinity;
        const bx = this.minX + (x + (stepX > 0 ? 1 : 0)) / this.inv;
        const bz = this.minZ + (z + (stepZ > 0 ? 1 : 0)) / this.inv;
        let tMaxX = dx !== 0 ? (bx - ox) / dx : Infinity;
        let tMaxZ = dz !== 0 ? (bz - oz) / dz : Infinity;
        // Bounded so a degenerate ray can never spin: the DDA can visit at most the
        // grid's Manhattan diameter worth of cells.
        const limit = this.nx + this.nz + 2;
        for (let i = 0; i < limit; i++) {
            const list = this.cells[z * this.nx + x];
            for (let j = 0; j < list.length; j++) {
                const b = list[j];
                if (b._qseg === qg)
                    continue; // already tested this box this query
                b._qseg = qg;
                if (hit(b))
                    return true;
            }
            if (x === ex && z === ez)
                return false;
            if (tMaxX < tMaxZ) {
                if (x === ex) {
                    tMaxX = Infinity;
                }
                else {
                    x += stepX;
                    tMaxX += tDeltaX;
                }
            }
            else {
                if (z === ez) {
                    tMaxZ = Infinity;
                }
                else {
                    z += stepZ;
                    tMaxZ += tDeltaZ;
                }
            }
            if (x < 0 || x >= this.nx || z < 0 || z >= this.nz)
                return false;
        }
        return false;
    }
}
/** Minimum horizontal span (both X and Z, metres) for a static box to count as
 *  a standable perch — used by `CollisionWorld.hookBoxes`, which a bot's escape
 *  grapple scans for the nearest hookable lip. A wall or thin ledge is narrower
 *  than this and can't be stood on, so it never belongs in the scan. */
export const HOOK_MIN_FOOTPRINT = 1.6;
/** Precomputed collision world for a map. */
export class CollisionWorld {
    aabbs;
    /** Swimmable water volumes (boxes tagged "water") — NOT solid collision;
     *  a submerged player gets swim physics inside them. */
    waters;
    /** RISING TIDE offset (metres): raises the effective water surface + volume
     *  top so the sea can flood decks mid-match. 0 on every map/at rest → the
     *  water behaves exactly as before. Server-authoritative, mirrored to clients
     *  via state.tideY so prediction agrees. */
    tideY = 0;
    pads;
    /** Map-authored rest position of each pad. A pad sitting on a moving island
     *  (e.g. the flying-tower grav-lift) is re-derived from the island pose each
     *  tick via syncMoverPads — this keeps its home so the offset stays fixed. */
    padHome;
    /** Moving-platform colliders (e.g. the train wagons), replaced every tick.
     *  Each carries its own per-tick movement delta so riders standing on it get
     *  carried along (server-authoritative). */
    dynamic = [];
    /** Object pool backing `dynamic` — see `pushDyn`. Reused across every rebuild
     *  so the per-tick/per-frame collider refresh allocates nothing. */
    dynPool = [];
    /** Hard play-space limits (shared by client prediction AND the server —
     *  if only one side clamps, every antigrav rise past the ceiling causes a
     *  reconcile-snap + full pending replay per ack, which can saturate the
     *  client main thread with several bots in the match). */
    ceilY;
    /** Zipline cables, precomputed with unit direction + length. Always oriented
     *  so a→b runs DOWNHILL, since you always ride toward the lower end. */
    ziplines;
    minX;
    maxX;
    minZ;
    maxZ;
    groundMinX;
    groundMaxX;
    groundMinZ;
    groundMaxZ;
    constructor(map) {
        // water volumes are pulled OUT of solid collision so players pass through
        // the surface and swim inside them (a solid seabed box below catches them)
        this.aabbs = map.boxes.filter((b) => b.tag !== "practice_mover" && b.tag !== "water").map(boxToAabb);
        this.waters = map.boxes.filter((b) => b.tag === "water").map(boxToAabb);
        this.pads = (map.jumpPads ?? []).map((jp) => ({
            x: jp.p[0], y: jp.p[1], z: jp.p[2], power: jp.power ?? MOVE.jumpPad.power,
            fling: jp.fling,
        }));
        this.padHome = this.pads.map((p) => ({ x: p.x, y: p.y, z: p.z }));
        this.ziplines = (map.ziplines ?? []).map((z) => {
            // orient a→b downhill so riding is always "toward b"
            const down = z.a[1] >= z.b[1];
            const a = down ? z.a : z.b;
            const b = down ? z.b : z.a;
            const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
            const len = Math.max(0.001, Math.hypot(dx, dy, dz));
            return {
                ax: a[0], ay: a[1], az: a[2], bx: b[0], by: b[1], bz: b[2],
                ux: dx / len, uy: dy / len, uz: dz / len, len,
            };
        });
        this.ceilY = map.bounds.max[1] + 85;
        // TWO boundary rings, by design (user call):
        // - AIRBORNE movement (jetpack glide, parachute, jumps) may roam a wide
        //   band past the edge — flying out is allowed; falling out ends in the
        //   void death like any fall. min/max = the wide air limit.
        // - GROUNDED movement and vehicles hit an INVISIBLE WALL (groundMin/Max)
        //   — you cannot walk or drive out. The wall sits 2m INSIDE bounds: the
        //   ground slabs end at bounds-2 on every map, and a ring outside the
        //   floor edge never stops anyone (you walk off the floor, go airborne,
        //   and sail past it — the original "no barrier on Rooftops" report).
        this.minX = map.bounds.min[0] - 60;
        this.maxX = map.bounds.max[0] + 60;
        this.minZ = map.bounds.min[2] - 60;
        this.maxZ = map.bounds.max[2] + 60;
        const inset = map.ringInset ?? 2; // arena 2x maps carry a scaled inset
        this.groundMinX = map.bounds.min[0] + inset;
        this.groundMaxX = map.bounds.max[0] - inset;
        this.groundMinZ = map.bounds.min[2] + inset;
        this.groundMaxZ = map.bounds.max[2] - inset;
    }
    grid = null;
    /**
     * Spatial index over `aabbs`, built on first use.
     *
     * Rebuilds itself if `aabbs.length` no longer matches what it was built from.
     * Doors and drawbridges splice/push colliders at runtime and every one of
     * those sites changes the length, so this catches a mutation whose author
     * forgot `invalidateColliderIndex()` — a stale index would silently drop a
     * closed door out of collision, which is exactly the kind of bug that is
     * invisible until someone walks through a wall.
     */
    get colliderIndex() {
        if (this.grid === null || this.grid.builtLength !== this.aabbs.length) {
            // ~6 m cells: a player AABB (0.8 m) touches 1-4 cells, and map geometry
            // is authored on roughly this scale, so cells stay well populated.
            this.grid = new ColliderGrid(this.aabbs, 6);
        }
        return this.grid;
    }
    hookBoxCache = null;
    hookBoxLen = -1;
    /**
     * Static subset of `aabbs` with a standable footprint (both horizontal spans
     * ≥ `HOOK_MIN_FOOTPRINT`) — the only boxes a bot's escape grapple can perch
     * on. Walls and thin ledges are filtered out here once, so the grapple's
     * nearest-lip scan iterates ~a fifth of the collider list on wall-heavy maps
     * (medieval_castle 962→300, pirate_ship 827→153) instead of every box on all
     * four attempts of a firing.
     *
     * Lazily built and length-guarded exactly like `colliderIndex`, and dropped
     * by the same `invalidateColliderIndex()` mutation hook (doors/drawbridges
     * splice `aabbs`). The returned boxes are the SAME references as in `aabbs`,
     * so identity checks against them still work.
     */
    get hookBoxes() {
        if (this.hookBoxCache === null || this.hookBoxLen !== this.aabbs.length) {
            this.hookBoxCache = this.aabbs.filter((b) => b.maxX - b.minX >= HOOK_MIN_FOOTPRINT && b.maxZ - b.minZ >= HOOK_MIN_FOOTPRINT);
            this.hookBoxLen = this.aabbs.length;
        }
        return this.hookBoxCache;
    }
    /** Drop the spatial index (and the hook-box subset) after mutating `aabbs`
     *  (doors, drawbridges). */
    invalidateColliderIndex() {
        this.grid = null;
        this.hookBoxCache = null;
    }
    /** Start a fresh moving-platform collider rebuild: empties `dynamic` (dropping
     *  references only — the pooled objects survive on `dynPool`) so the ensuing
     *  `pushDyn` calls can index the pool by `dynamic.length`. Callers that already
     *  did `dynamic.length = 0` inline are equivalent; this just names the contract. */
    beginDynamic() {
        this.dynamic.length = 0;
    }
}
/**
 * If the player is standing on a moving platform, slide them by that platform's
 * per-tick delta (so they ride along). Call once per server tick, before
 * processing the player's own movement. Returns true if carried.
 *
 * `scale` re-times the baked-in delta. The deltas in `world.dynamic` cover
 * whatever interval the caller advanced its platform clock by — one server tick
 * on the server, one RENDER FRAME on the client. Client reconciliation replays
 * one carry per pending input (a fixed 1/60 s of sim time each), so it must ask
 * for `SIM_DT / frameDt` of the frame's delta or it silently integrates the
 * platform at the monitor's refresh rate. Default 1 = "the delta as baked",
 * which is what the server and the live client frame both want.
 */
export function applyPlatformCarry(world, k, scale = 1) {
    const hw = MOVE.playerHalfWidth;
    for (const b of world.dynamic) {
        if (k.x > b.minX - hw && k.x < b.maxX + hw &&
            k.z > b.minZ - hw && k.z < b.maxZ + hw &&
            k.y >= b.maxY - 1.4 && k.y <= b.maxY + 1.2) { // wide window: fast elevators move >0.5/tick
            if (b.dyaw !== undefined) {
                // exact rigid-body carry: rotate about the deck centre + translate.
                // Scaled: spin by a fraction of the yaw step about a centre lerped the
                // same fraction of the way — identity at scale 0, exact at scale 1.
                const cx = b.rotCx ?? 0, cz = b.rotCz ?? 0;
                const lx = k.x - cx, lz = k.z - cz;
                const c = Math.cos(b.dyaw * scale), sn = Math.sin(b.dyaw * scale);
                k.x = cx + ((b.rotCx2 ?? 0) - cx) * scale + lx * c - lz * sn;
                k.z = cz + ((b.rotCz2 ?? 0) - cz) * scale + lx * sn + lz * c;
                k.y += b.dy * scale;
            }
            else {
                k.x += b.dx * scale;
                k.z += b.dz * scale;
                k.y += b.dy * scale; // carry along (incl. altitude)
            }
            // a RISING platform must lift the rider, never swallow them
            if (k.y < b.maxY) {
                k.y = b.maxY;
                if (k.vy < 0)
                    k.vy = 0;
                k.onGround = true;
            }
            return true;
        }
    }
    return false;
}
/** Surface height (maxY) of the deepest-reaching water volume the player's
 *  column is inside, or null if not over/in any water. */
export function waterSurfaceAt(world, x, y, z, height) {
    let top = null;
    for (const w of world.waters) {
        if (x < w.minX || x > w.maxX || z < w.minZ || z > w.maxZ)
            continue;
        // RISING TIDE: the surface (and the volume's top) rides up by world.tideY,
        // so a high tide floods the decks. tideY is 0 everywhere else → unchanged.
        const surf = w.maxY + world.tideY;
        // the player's body [y, y+height] must overlap the (tide-raised) water volume
        if (y > surf || y + height < w.minY)
            continue;
        if (top === null || surf > top)
            top = surf;
    }
    return top;
}
/** Scratch for `nearestStaticHit`: a module-scope ray reference + running
 *  minimum, so the indexed path allocates NO closure per call — `resolveHitscan`
 *  runs this once per pellet per shot, and stepRockets/stepKnives once per
 *  projectile per tick. */
let _nshRay = { ox: 0, oy: 0, oz: 0, dx: 0, dy: 0, dz: 0 };
let _nshNearest = Infinity;
const _nshScan = (b) => {
    const d = rayAabb(_nshRay, b);
    if (d !== null && d < _nshNearest)
        _nshNearest = d;
    return false; // never early-exit: we need the true minimum, not the first hit
};
/**
 * Nearest entry distance along `ray` to any STATIC collider (`world.aabbs`), or
 * `fallback` if none is closer than `fallback`. Does NOT test `world.dynamic`
 * (moving platforms) — callers needing those keep their own small scan and pass
 * the result back in as the next `fallback`, or scan dynamic separately.
 *
 * Routes through the collider spatial index (invariant 9): the 2D DDA visits a
 * conservative SUPERSET of the boxes the ray can hit, and `min` is idempotent
 * under the index's duplicate cell listings, so the returned distance is
 * bit-identical to the linear `for (const b of aabbs)` scan it replaces.
 * `agent/bench-rayhit.mjs` asserts that equivalence over every map.
 */
export function nearestStaticHit(world, ray, fallback = Infinity) {
    _nshRay = ray;
    _nshNearest = fallback;
    const idx = world.colliderIndex;
    if (idx.passthrough) {
        // Small maps keep the plain linear scan (see `collides` — the index's
        // per-box callback indirection is a net loss below the crossover).
        for (const b of world.aabbs) {
            const d = rayAabb(ray, b);
            if (d !== null && d < _nshNearest)
                _nshNearest = d;
        }
        return _nshNearest;
    }
    // A far endpoint drives the DDA the length of the ray across the grid; cx/cz
    // clamp it to the grid edge and the walk terminates at the boundary. A near-
    // vertical ray (dx≈dz≈0) collapses to the origin cell, which is exactly the
    // only cell whose footprint a vertical ray can cross.
    const FAR = 4096;
    // Only walk the grid as far as a hit could POSSIBLY matter. `_nshNearest`
    // starts at `fallback` and `_nshScan` only ever lowers it, so a box whose ray
    // entry is beyond `fallback` can never beat the running min — visiting its
    // cell is wasted DDA work. And a box that COULD beat it contains the ray's
    // entry point at distance < fallback, so that point's cell lies ON the
    // clamped segment and is still visited. The clamp is therefore BIT-IDENTICAL
    // to the full-length walk (agent/bench-rayhit.mjs asserts it), while sparing
    // the cells past the useful range: a 3 m rocket/knife step or a short hose
    // stream no longer DDA-walks the whole map. Infinite fallback (open hitscan)
    // keeps the full-map reach. `Math.max(0, …)` guards a 0/negative fallback so
    // the endpoint never runs backwards past the origin.
    const reach = Number.isFinite(fallback) ? Math.min(FAR, Math.max(0, fallback)) : FAR;
    idx.anyOnSegment(ray.ox, ray.oz, ray.ox + ray.dx * reach, ray.oz + ray.dz * reach, _nshScan);
    return _nshNearest;
}
/** Scratch for `staticBoxOverlap`: a module-scope query AABB + its predicate,
 *  so the indexed path allocates no closure per call. */
const _sboBox = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };
const _sboHits = (b) => _sboBox.minX < b.maxX && _sboBox.maxX > b.minX &&
    _sboBox.minY < b.maxY && _sboBox.maxY > b.minY &&
    _sboBox.minZ < b.maxZ && _sboBox.maxZ > b.minZ;
/**
 * Does the query AABB overlap ANY static collider (`world.aabbs`)? A boolean
 * any-hit — exactly the shape the collider index is valid for (invariant 9),
 * with no dedup needed. Does NOT test `world.dynamic`. Bit-identical to the
 * linear scan it replaces (`agent/bench-rayhit.mjs` covers the overlap path).
 */
export function staticBoxOverlap(world, minX, minY, minZ, maxX, maxY, maxZ) {
    const idx = world.colliderIndex;
    if (idx.passthrough) {
        for (const b of world.aabbs) {
            if (minX < b.maxX && maxX > b.minX && minY < b.maxY &&
                maxY > b.minY && minZ < b.maxZ && maxZ > b.minZ)
                return true;
        }
        return false;
    }
    _sboBox.minX = minX;
    _sboBox.maxX = maxX;
    _sboBox.minY = minY;
    _sboBox.maxY = maxY;
    _sboBox.minZ = minZ;
    _sboBox.maxZ = maxZ;
    return idx.anyInAabb(minX, minZ, maxX, maxZ, _sboHits);
}
/** Scratch player AABB + its overlap predicate. Both are module singletons so
 *  the hot `collides` path allocates neither a box nor a closure per call —
 *  `collides` runs 3-4x per step on a clear frame and 20-45x when wedged. */
const _probe = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };
const _probeHits = (b) => overlaps(_probe, b);
function overlaps(a, b) {
    return (a.minX < b.maxX && a.maxX > b.minX &&
        a.minY < b.maxY && a.maxY > b.minY &&
        a.minZ < b.maxZ && a.maxZ > b.minZ);
}
function collides(world, k, height) {
    const hw = MOVE.playerHalfWidth;
    const minX = k.x - hw, maxX = k.x + hw;
    const minY = k.y, maxY = k.y + height;
    const minZ = k.z - hw, maxZ = k.z + hw;
    // Static geometry goes through the spatial index; `world.dynamic` (moving
    // platforms, ~40 boxes, rebuilt every tick) stays a linear scan — it is small
    // and re-indexing it per tick would cost more than it saves.
    const idx = world.colliderIndex;
    if (idx.passthrough) {
        // Small maps keep the plain scan, written against LOCALS rather than the
        // `_probe` scratch below. That is not cosmetic: routing this path through a
        // module-scope object defeats V8's escape analysis and measured 0.82-0.88x
        // (i.e. slower than before the index existed) on the smallest maps —
        // agent/bench-ab.mjs is the A/B that caught it.
        for (const box of world.aabbs) {
            if (minX < box.maxX && maxX > box.minX && minY < box.maxY &&
                maxY > box.minY && minZ < box.maxZ && maxZ > box.minZ)
                return true;
        }
    }
    else {
        _probe.minX = minX;
        _probe.maxX = maxX;
        _probe.minY = minY;
        _probe.maxY = maxY;
        _probe.minZ = minZ;
        _probe.maxZ = maxZ;
        if (idx.anyInAabb(minX, minZ, maxX, maxZ, _probeHits))
            return true;
    }
    for (const box of world.dynamic) {
        if (minX < box.maxX && maxX > box.minX && minY < box.maxY &&
            maxY > box.minY && minZ < box.maxZ && maxZ > box.minZ)
            return true;
    }
    return false;
}
// ---- GHOST CLOAK phasing (owner 2026-10-28: "with the ghost powerup I can
// cross walls — be careful, not floors"). Boolean any-hit queries only, so
// the spatial index is valid for all three (invariant 9).
/** Any STATIC box overlapping the player. (Dynamic platforms excluded: they
 *  move into players by design and the carry code owns that case.) */
function overlapsStatic(world, k, height) {
    const hw = MOVE.playerHalfWidth;
    return staticBoxOverlap(world, k.x - hw, k.y, k.z - hw, k.x + hw, k.y + height, k.z + hw);
}
const _gNow = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };
let _gY0 = 0;
/** Overlaps the player box now but NOT at feet height `_gY0` (same x/z): a box
 *  the move ENTERED, rather than a wall the ghost was already standing in. */
const _gEntering = (b) => overlaps(_gNow, b)
    && !(_gY0 < b.maxY && _gY0 + (_gNow.maxY - _gNow.minY) > b.minY);
function collidesEntering(world, k, height, y0) {
    const hw = MOVE.playerHalfWidth;
    _gNow.minX = k.x - hw;
    _gNow.maxX = k.x + hw;
    _gNow.minY = k.y;
    _gNow.maxY = k.y + height;
    _gNow.minZ = k.z - hw;
    _gNow.maxZ = k.z + hw;
    _gY0 = y0;
    const idx = world.colliderIndex;
    if (idx.passthrough) {
        for (const b of world.aabbs)
            if (_gEntering(b))
                return true;
    }
    else if (idx.anyInAabb(_gNow.minX, _gNow.minZ, _gNow.maxX, _gNow.maxZ, _gEntering))
        return true;
    for (const b of world.dynamic)
        if (_gEntering(b))
            return true;
    return false;
}
/** A floor top within a hand of the player's feet at the current x/z — the
 *  ghost may only phase sideways where there IS ground on the far side, so a
 *  roof edge or a tall block over a void stays solid (no falling out of maps). */
const _gSup = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };
const _gSupports = (b) => overlaps(_gSup, b) && b.maxY <= _gSup.maxY && b.maxY >= _gSup.minY;
function supportedAt(world, k) {
    // a thin probe under the body's CENTRE: half a body over a roof edge is
    // already over the void
    const hw = 0.05;
    _gSup.minX = k.x - hw;
    _gSup.maxX = k.x + hw;
    _gSup.minZ = k.z - hw;
    _gSup.maxZ = k.z + hw;
    _gSup.minY = k.y - 0.2;
    _gSup.maxY = k.y + 0.05;
    const idx = world.colliderIndex;
    if (idx.passthrough) {
        for (const b of world.aabbs)
            if (_gSupports(b))
                return true;
    }
    else if (idx.anyInAabb(_gSup.minX, _gSup.minZ, _gSup.maxX, _gSup.maxZ, _gSupports))
        return true;
    for (const b of world.dynamic)
        if (_gSupports(b))
            return true;
    return false;
}
/** Cloak ended inside a wall: move to the nearest free spot (fixed ring
 *  order — deterministic on both sides). Only runs while overlapping. */
const EJECT_DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [0.7071, 0.7071], [-0.7071, 0.7071], [0.7071, -0.7071], [-0.7071, -0.7071]];
function ejectFromStatic(world, k, height) {
    const x0 = k.x, z0 = k.z;
    for (let r = 0.25; r <= 6; r += 0.25) {
        for (const [ex, ez] of EJECT_DIRS) {
            k.x = x0 + ex * r;
            k.z = z0 + ez * r;
            if (!collides(world, k, height)) {
                k.vx = 0;
                k.vz = 0;
                return;
            }
        }
    }
    k.x = x0;
    k.z = z0; // boxed in everywhere — leave it rather than teleport far
}
export function playerHeight(k) {
    return k.crouching ? MOVE.crouchHeight : MOVE.standHeight;
}
export function eyeHeight(k) {
    return k.crouching ? MOVE.crouchEye : MOVE.standEye;
}
/**
 * Advance one player by one input frame. Mutates and returns `k`.
 * dt comes from the input (clamped) so client and server integrate the
 * exact same steps.
 */
export function stepPlayer(k, input, world, 
/** OVERDRIVE power-up: both sides pass the same replicated value, so
 *  prediction stays consistent (same pattern as the jetpack glide gate). */
speedMul = 1, 
/** Paraglider deployed (no jetpack, Glide held, airborne) — slow fall. */
chute = false, 
/** ANTIGRAV power-up: both sides pass the same replicated value (moon
 *  gravity — higher floatier jumps, slow falls). */
gravityMul = 1, 
/** RUBBER modifier restitution (0 = STANDARD, no bounce). Both sides pass the
 *  same replicated value — see modBounce; the landing branch below rebounds a
 *  hard fall by this fraction, so the whole floor reads as a trampoline. */
bounce = 0, 
/** GHOST CLOAK active: walk through walls (not floors) — both sides pass the
 *  same replicated `cloakUntil` test, like speedMul. */
ghost = false) {
    const dt = Math.min(MOVE.maxDtMs, Math.max(MOVE.minDtMs, input.dtMs)) / 1000;
    // the cloak ran out while phased into a wall: pop out to the nearest gap
    if (!ghost && overlapsStatic(world, k, playerHeight(k)))
        ejectFromStatic(world, k, playerHeight(k));
    if (chute)
        speedMul *= (input.buttons & Buttons.Sprint) !== 0 ? 2.7 : 1.7; // canopy drift; L3/Sprint = faster glide
    // -- crouch state (hold-to-crouch; only stand with headroom)
    const wantCrouch = (input.buttons & Buttons.Crouch) !== 0;
    if (wantCrouch !== k.crouching) {
        if (wantCrouch) {
            // SLIDE launch: crouching while sprinting fast on the ground boosts the
            // current momentum, then bleeds off (see slideFriction below).
            const hsp = Math.sqrt(k.vx * k.vx + k.vz * k.vz);
            const sprintHeld = (input.buttons & Buttons.Sprint) !== 0;
            // boost only — if you're already faster than slideSpeed (pad fling,
            // glide landing), crouching must never BLEED that momentum
            if (k.onGround && sprintHeld && hsp > MOVE.walkSpeed * 0.9 && hsp < MOVE.slideSpeed) {
                const s = MOVE.slideSpeed / hsp;
                k.vx *= s;
                k.vz *= s;
            }
            k.crouching = true;
        }
        else {
            k.crouching = false;
            if (collides(world, k, MOVE.standHeight))
                k.crouching = true; // no headroom
        }
    }
    // sliding = crouched + grounded + still carrying momentum above crouch pace
    const sliding = k.crouching && k.onGround && (k.vx * k.vx + k.vz * k.vz) > MOVE.slideMinSpeed * MOVE.slideMinSpeed;
    // -- wish direction from yaw + move axes (client never sends velocity)
    let mx = input.moveX;
    let mz = input.moveZ;
    const mSq = mx * mx + mz * mz;
    if (mSq > 1) {
        const mLen = Math.sqrt(mSq);
        mx /= mLen;
        mz /= mLen;
    }
    const sinY = Math.sin(input.yaw);
    const cosY = Math.cos(input.yaw);
    // forward = (-sinYaw, -cosYaw), right = (cosYaw, -sinYaw) — Three.js convention
    const wishX = -sinY * mz + cosY * mx;
    const wishZ = -cosY * mz - sinY * mx;
    const antigrav = gravityMul < 0;
    const sprinting = (input.buttons & Buttons.Sprint) !== 0 && !k.crouching && mz > 0;
    const ads = (input.buttons & Buttons.ADS) !== 0;
    let speed = k.crouching ? MOVE.crouchSpeed : sprinting ? MOVE.sprintSpeed : MOVE.walkSpeed;
    if (ads)
        speed *= MOVE.adsSpeedMult;
    if (antigrav)
        speed *= MOVE.antigravSpeedMult;
    speed *= speedMul;
    // -- WATER: detect submersion (drives swim physics + underwater visuals).
    // inWater = feet wet (wading); submerged = head under → full 3D swim.
    // HYSTERESIS on `submerged`: the underwater view/physics only toggle once the
    // eye is clearly past the surface (±0.18 m), so bobbing exactly at the
    // waterline can't flicker between the above-water and underwater view.
    const bodyH = playerHeight(k);
    const wSurf = world.waters.length ? waterSurfaceAt(world, k.x, k.y, k.z, bodyH) : null;
    const eyeY = k.y + eyeHeight(k);
    k.inWater = wSurf !== null && k.y < wSurf - 0.05;
    // PHYSICS uses a HARD threshold (head under the surface) so client prediction
    // and the server authority agree bit-exact during reconcile — a hysteresis
    // that depends on the previous frame diverged on replay and could stall the
    // socket ("lost connection when I go underwater").
    const swimming = wSurf !== null && eyeY < wSurf && !antigrav && !chute;
    // VISUAL flag carries ±0.18 m hysteresis so the underwater tint can't flicker
    // exactly at the waterline (view only — never gates physics).
    if (wSurf === null)
        k.submerged = false;
    else if (k.submerged)
        k.submerged = eyeY < wSurf + 0.18;
    else
        k.submerged = eyeY < wSurf - 0.18;
    if (swimming)
        speed = MOVE.swim.speed * speedMul; // even, drag-limited pace
    else if (k.inWater)
        speed *= 0.62; // wading through shallows
    // -- horizontal accel: exponential approach (frame-rate independent).
    // While sliding, use a low rate so momentum bleeds off slowly (and steering
    // is limited), which gives the slide its slippery feel.
    let rate = antigrav ? MOVE.groundAccel : !k.onGround ? MOVE.airAccel : sliding ? MOVE.slideFriction : MOVE.groundAccel;
    if (swimming)
        rate = 6; // responsive swim steering + water drag when you stop
    const blend = 1 - Math.exp(-rate * dt);
    k.vx += (wishX * speed - k.vx) * blend;
    k.vz += (wishZ * speed - k.vz) * blend;
    // BUNNY-HOP damping: slide-jump chains re-boosted to full slide speed on
    // every landing and cruised ~40% over sprint indefinitely. Airborne speed
    // above the sprint cap now bleeds gently — a single slide or pad fling
    // still feels fast, but hop chains settle ~10-15% over sprint.
    if (!k.onGround && !chute && !swimming) {
        const hopCap = Math.max(speed, MOVE.sprintSpeed) * 1.12;
        const hsp2Sq = k.vx * k.vx + k.vz * k.vz;
        if (hsp2Sq > hopCap * hopCap) {
            const hsp2 = Math.sqrt(hsp2Sq);
            const f = Math.max(hopCap / hsp2, 1 - 1.6 * dt);
            k.vx *= f;
            k.vz *= f;
        }
    }
    // -- jump / gravity  (WATER replaces this while submerged or floating)
    const jumpHeld = (input.buttons & Buttons.Jump) !== 0;
    const crouchHeld = (input.buttons & Buttons.Crouch) !== 0;
    // stable float line: idle in water, you settle here — eyes clearly ABOVE
    // the surface — via a damped spring, so the view holds steady (no bobbing,
    // no half-submerged waterline flicker).
    const floatY = wSurf !== null ? wSurf - eyeHeight(k) + 0.45 : 0;
    if (swimming) {
        // 3D SWIM with NEUTRAL BUOYANCY: with no vertical input you HOVER at your
        // current depth (so you can stay under to explore/fight), NOT get yanked
        // to the surface. Crouch dives, Jump rises toward the surface, and swimming
        // forward follows your LOOK pitch (aim down to descend).
        const [, lookY] = yawPitchToDir(input.yaw, input.pitch); // dy: +up, −down
        const forward = Math.max(0, mz);
        let targetVy;
        if (crouchHeld)
            targetVy = -MOVE.swim.down; // DIVE to the seabed (ground)
        else if (jumpHeld)
            targetVy = MOVE.swim.up; // kick UP to the surface
        else {
            // idle: a GENTLE positive buoyancy so you slowly FLOAT back toward the
            // surface (real water) — you're never stuck hovering in mid-water. Aiming
            // DOWN while swimming forward still descends (net negative), so you keep
            // full dive control; it just takes light effort to stay under.
            targetVy = MOVE.swim.buoyancy + lookY * forward * MOVE.swim.lookVert;
        }
        k.vy += (targetVy - k.vy) * (1 - Math.exp(-5 * dt));
        if (k.vy < -MOVE.swim.down * 2.2)
            k.vy = -MOVE.swim.down * 2.2; // cap plunge
        // GROUND ON THE SEABED: sinking onto a solid floor lets you stand/walk it
        // (don't force onGround false — let the collision pass keep you grounded
        // when a surface is right under your feet, so "be on the ground" works).
        if (k.vy > 0.05)
            k.onGround = false;
    }
    else if (k.inWater && !chute && gravityMul >= 0) {
        // FEET WET, head above the surface: rest STABLE on the float line (view
        // steady, eyes clear of the water). Jump leaps you out toward a ramp;
        // to DIVE, either hold Crouch OR swim forward while aiming DOWN (so touch
        // players, who have no crouch, can duck under by looking down + moving).
        const [, lookY2] = yawPitchToDir(input.yaw, input.pitch);
        const divingLook = mz > 0.1 && lookY2 < -0.2;
        if (jumpHeld && k.onGround) {
            // Shallow water must not steal the ordinary ground jump. Pirate-ship
            // tides can put only the player's feet underwater while they are still
            // standing on deck; routing that state exclusively through swim lift
            // left vy at zero and made Jump do nothing.
            k.vy = MOVE.jumpVel;
            k.onGround = false;
        }
        else if (jumpHeld && k.vy < MOVE.swim.up)
            k.vy = MOVE.swim.up;
        else if (crouchHeld || divingLook)
            k.vy -= MOVE.gravity * 0.75 * dt;
        else
            k.vy += (((floatY - k.y) * 3.5) - k.vy) * (1 - Math.exp(-6 * dt));
    }
    else {
        if (!antigrav && k.onGround && jumpHeld) {
            k.vy = MOVE.jumpVel;
            k.onGround = false;
        }
        if (chute) {
            // paraglider: settle into a gentle descent (BotW paravoile feel)
            k.vy += (-3.2 - k.vy) * Math.min(1, 6 * dt);
        }
        else if (gravityMul < 0) {
            // ANTIGRAV = REVERSED gravity: a steady upward pull.
            const RISE_MAX = 13;
            if (k.vy < RISE_MAX)
                k.vy = Math.min(RISE_MAX, k.vy + MOVE.gravity * -gravityMul * dt);
        }
        else {
            // falls are snappier than jumps: extra gravity ONLY on the way down.
            k.vy -= MOVE.gravity * gravityMul * (k.vy < 0 ? 1.35 : 1) * dt;
        }
    }
    // ---- ZIPLINES -----------------------------------------------------------
    // Catch a cable by passing within `grab` of it while airborne, ride to the
    // low end, Jump to drop off with momentum. Position is driven along the
    // cable (not velocity-integrated) so client and server land on identical
    // coordinates every tick.
    if ((k.zipCd ?? 0) > 0)
        k.zipCd = Math.max(0, (k.zipCd ?? 0) - dt);
    const zipJump = (input.buttons & Buttons.Jump) !== 0;
    if ((k.zipIdx ?? -1) >= 0) {
        const c = world.ziplines?.[k.zipIdx];
        if (!c) {
            k.zipIdx = -1;
        }
        else {
            k.zipDist = (k.zipDist ?? 0) + MOVE.zip.speed * dt;
            const done = k.zipDist >= c.len;
            const d = Math.min(k.zipDist, c.len);
            k.x = c.ax + c.ux * d;
            k.y = c.ay + c.uy * d - MOVE.zip.hang;
            k.z = c.az + c.uz * d;
            // carry the cable's direction as real velocity so letting go launches you
            k.vx = c.ux * MOVE.zip.speed;
            k.vy = c.uy * MOVE.zip.speed;
            k.vz = c.uz * MOVE.zip.speed;
            k.onGround = false;
            // You attach BY jumping into the cable, so Jump is still held on the
            // next tick — a level-triggered detach ended every ride after ~0.9m of a
            // 37m cable, and anyone holding space could never zipline at all.
            // Require a little travel before Jump can let go. Measured from where
            // this ride STARTED: catching a cable 28m along one (jumping into the
            // low run of the fore-mast line from the upper deck) starts zipDist at
            // 28, so an absolute `> 3` test dropped you on the very first tick.
            if (done || (zipJump && k.zipDist - (k.zipStart ?? 0) > 3)) {
                k.zipIdx = -1;
                k.zipCd = MOVE.zip.cooldown;
                if (zipJump) {
                    // a deliberate hop off keeps the cable's momentum, plus a little lift
                    k.vx *= MOVE.zip.exit;
                    k.vz *= MOVE.zip.exit;
                    k.vy = Math.max(k.vy, 0) + MOVE.jumpVel * 0.55;
                }
                else {
                    // Running out the END is an arrival, not a launch: at full 18 m/s the
                    // rider sailed clean over the bow into the sea. Bleed it off so they
                    // drop onto the deck the cable actually points at.
                    k.vx *= 0.3;
                    k.vz *= 0.3;
                    k.vy = Math.min(k.vy, 0);
                }
            }
        }
    }
    else if (!k.onGround && (k.zipCd ?? 0) <= 0 && (world.ziplines?.length ?? 0) > 0) {
        const hx = k.x, hy = k.y + eyeHeight(k), hz = k.z;
        let bestI = -1, bestD2 = MOVE.zip.grab * MOVE.zip.grab, bestT = 0;
        for (let i = 0; i < world.ziplines.length; i++) {
            const c = world.ziplines[i];
            // project the head onto the cable segment
            const t = Math.max(0, Math.min(c.len, (hx - c.ax) * c.ux + (hy - c.ay) * c.uy + (hz - c.az) * c.uz));
            const px = c.ax + c.ux * t, py = c.ay + c.uy * t, pz = c.az + c.uz * t;
            const d2 = (hx - px) ** 2 + (hy - py) ** 2 + (hz - pz) ** 2;
            if (d2 < bestD2) {
                bestD2 = d2;
                bestI = i;
                bestT = t;
            }
        }
        // don't catch it right at the bottom end — nothing left to ride
        if (bestI >= 0 && world.ziplines[bestI].len - bestT > 1.5) {
            k.zipIdx = bestI;
            k.zipDist = bestT;
            k.zipStart = bestT;
        }
    }
    if ((k.zipIdx ?? -1) >= 0) {
        // riding: skip the rest of the vertical/collision pipeline this tick
        k.grapOn = false;
        return k;
    }
    // ---- GRAPPLING HOOK -----------------------------------------------------
    applyGrapple(k, input, world, dt);
    // terminal velocity: long falls must not outrun the collision substeps
    if (k.vy < -48)
        k.vy = -48;
    // NB: must clear the STRONGEST jump pad (the violet mega-beams now launch
    // at 62 m/s — Skyhaven undercity + Rooftops XL) — a low cap silently shaves
    // their apex ("the purple beam takes me less high"); this bug regressed
    // once already at 36 and again at 60. Antigrav rise is capped separately.
    if (k.vy > 66)
        k.vy = 66;
    // hard ceiling + side limits — IDENTICAL on client and server so prediction
    // never diverges at the boundary (see CollisionWorld.ceilY note).
    // AIRBORNE movement (jumps, jump pads, parachute; jetpack in stepGlide) may
    // cross into the wide band past the edge — by design. Only FEET ON THE
    // GROUND (and vehicles, in stepVehicle) are pinned to the tight ring:
    // you can leap out, but you can't WALK out.
    if (k.y > world.ceilY) {
        k.y = world.ceilY;
        if (k.vy > 0)
            k.vy = 0;
    }
    if (!k.onGround || chute) {
        if (k.x < world.minX) {
            k.x = world.minX;
            if (k.vx < 0)
                k.vx = 0;
        }
        if (k.x > world.maxX) {
            k.x = world.maxX;
            if (k.vx > 0)
                k.vx = 0;
        }
        if (k.z < world.minZ) {
            k.z = world.minZ;
            if (k.vz < 0)
                k.vz = 0;
        }
        if (k.z > world.maxZ) {
            k.z = world.maxZ;
            if (k.vz > 0)
                k.vz = 0;
        }
    }
    else {
        // snap back only when NEAR the line (per-step overshoot is < SNAP even at
        // slide speed). Someone who LANDED outside (jumped/flew out) must not be
        // teleported back to the ring — they can walk back in (or fall), but the
        // zeroed outward velocity means they can never push further out on foot.
        const SNAP = 0.8;
        if (k.x < world.groundMinX) {
            if (k.x > world.groundMinX - SNAP)
                k.x = world.groundMinX;
            if (k.vx < 0)
                k.vx = 0;
        }
        if (k.x > world.groundMaxX) {
            if (k.x < world.groundMaxX + SNAP)
                k.x = world.groundMaxX;
            if (k.vx > 0)
                k.vx = 0;
        }
        if (k.z < world.groundMinZ) {
            if (k.z > world.groundMinZ - SNAP)
                k.z = world.groundMinZ;
            if (k.vz < 0)
                k.vz = 0;
        }
        if (k.z > world.groundMaxZ) {
            if (k.z < world.groundMaxZ + SNAP)
                k.z = world.groundMaxZ;
            if (k.vz > 0)
                k.vz = 0;
        }
    }
    const height = playerHeight(k);
    // -- integrate, axis-separated, with step-up on horizontal block
    const tryAxis = (axis, delta) => {
        if (delta === 0)
            return;
        const before = k[axis];
        k[axis] += delta;
        if (!collides(world, k, height))
            return;
        // blocked — try stepping up (stairs) when grounded
        if (k.onGround) {
            const beforeY = k.y;
            k.y += MOVE.stepHeight;
            if (!collides(world, k, height)) {
                // settle down onto the step
                let drop = 0;
                while (drop < MOVE.stepHeight) {
                    k.y -= 0.05;
                    drop += 0.05;
                    if (collides(world, k, height)) {
                        k.y += 0.05;
                        return;
                    }
                }
                return;
            }
            k.y = beforeY;
        }
        // GHOST: walk on through the wall — only on foot, and only where there is
        // floor at the same height on the far side (never off a ledge or roof)
        if (ghost && k.onGround && supportedAt(world, k))
            return;
        // slide: binary-search the max non-colliding movement, kill velocity
        let lo = 0;
        let hi = delta;
        for (let i = 0; i < 8; i++) {
            const mid = (lo + hi) / 2;
            k[axis] = before + mid;
            if (collides(world, k, height))
                hi = mid;
            else
                lo = mid;
        }
        k[axis] = before + lo;
        if (axis === "x")
            k.vx = 0;
        else
            k.vz = 0;
    };
    // Fast inherited velocity (afterburner release / jump-pad launch) can cover
    // more than a thin wall in one 50 ms input. Substep only when needed; normal
    // running remains one identical x-then-z pass.
    const horizontalSteps = Math.max(1, Math.ceil((Math.max(Math.abs(k.vx), Math.abs(k.vz)) * dt) / 0.6));
    const horizontalDt = dt / horizontalSteps;
    for (let i = 0; i < horizontalSteps; i++) {
        tryAxis("x", k.vx * horizontalDt);
        tryAxis("z", k.vz * horizontalDt);
    }
    // vertical — SUBSTEPPED so fast falls / grav-lift launches can't tunnel
    // through thin slabs (sky bridges are 0.3 m; an endpoint-only check skips
    // anything inside |vy|·dt − height). Deterministic on client + server.
    let dyLeft = k.vy * dt;
    while (dyLeft !== 0) {
        const dy = Math.sign(dyLeft) * Math.min(Math.abs(dyLeft), 1.2);
        dyLeft -= dy;
        const beforeY = k.y;
        k.y += dy;
        // GHOST: vertically only boxes we're NOT already inside count — the wall
        // we phased into doesn't hold us up, but floors and ceilings still do
        if (ghost ? collidesEntering(world, k, height, beforeY) : collides(world, k, height)) {
            let lo = 0;
            let hi = dy;
            for (let i = 0; i < 8; i++) {
                const mid = (lo + hi) / 2;
                k.y = beforeY + mid;
                if (ghost ? collidesEntering(world, k, height, beforeY) : collides(world, k, height))
                    hi = mid;
                else
                    lo = mid;
            }
            k.y = beforeY + lo;
            // RUBBER: a hard DOWNWARD landing rebounds instead of sticking. `k.vy` is
            // still the impact velocity here (zeroed just below on the normal path), so
            // a fall faster than bounceMinSpeed springs back up at `bounce` restitution
            // and stays airborne (onGround false) — the trampoline read. Everything
            // else (gentle landings, ceiling hits `dy>0`) keeps the STANDARD path, so
            // with bounce=0 this branch is byte-identical to the old code.
            if (dy < 0 && bounce > 0 && k.vy < -MOVE.bounceMinSpeed) {
                k.vy = -k.vy * bounce;
                // leave onGround false — the player is launched, not resting
            }
            else {
                if (dy < 0)
                    k.onGround = true;
                k.vy = 0;
            }
            break;
        }
        else if (dy < 0) {
            k.onGround = false;
        }
    }
    // -- bounce pads: standing on one launches you skyward + forward, so you
    // arc OFF the pad instead of landing back on it and bouncing forever.
    // fling-0 pads are GRAV-BEAM COLUMNS instead (handled just below) — a
    // one-shot launch through a tight stairwell shaft got riders stuck on
    // floor-hole edges whenever they held a movement key.
    if (k.onGround && k.vy < 2 && world.pads.length > 0) {
        const jp = MOVE.jumpPad;
        for (const pad of world.pads) {
            if ((pad.fling ?? jp.fling) === 0)
                continue; // beam columns handled below
            if (Math.abs(k.x - pad.x) < jp.radius &&
                Math.abs(k.z - pad.z) < jp.radius &&
                k.y >= pad.y - 0.4 && k.y <= pad.y + jp.height) {
                // under ANTIGRAV, an unscaled launch overshoots ~4x (v²/2g) and you
                // hang for most of the event — scale so pads reach their DESIGNED
                // height regardless of the current gravity
                k.vy = pad.power * Math.sqrt(Math.max(0.25, gravityMul)); // clamp: reversed gravity would NaN the sqrt
                const fling = pad.fling ?? jp.fling;
                if (k.vx * k.vx + k.vz * k.vz < fling * fling) {
                    // fling in the look direction (or keep existing momentum if faster)
                    k.vx = -Math.sin(input.yaw) * fling;
                    k.vz = -Math.cos(input.yaw) * fling;
                }
                k.onGround = false;
                break;
            }
        }
    }
    // -- grav-beam columns (fling-0 pads): a MAGNETIC beam. Anywhere inside the
    // column it carries you steadily upward and re-centres you on the shaft —
    // hold any movement key and you still thread every floor hole dead centre,
    // and you can never fall back down the shaft. It releases you just above
    // the roof opening with a small pop so you can step off.
    if (world.pads.length > 0) {
        const jp = MOVE.jumpPad;
        for (const pad of world.pads) {
            if ((pad.fling ?? jp.fling) !== 0)
                continue;
            if (Math.abs(k.x - pad.x) > jp.radius || Math.abs(k.z - pad.z) > jp.radius)
                continue;
            const apexH = (pad.power * pad.power) / (2 * MOVE.gravity);
            const rel = k.y - pad.y;
            if (rel < -0.5 || rel > apexH - 2.5)
                continue;
            // carry speed scales with the pad's power — stronger beams are FASTER
            // rides (the old fixed 11 m/s is why boosted lifts still felt slow)
            k.vy = Math.max(k.vy, Math.max(11, pad.power * 0.55));
            k.vx = (pad.x - k.x) * 3; // re-centre spring beats air-control drift
            k.vz = (pad.z - k.z) * 3;
            k.onGround = false;
            break;
        }
    }
    return k;
}
/** Shared hook step used by ordinary movement AND jetpack flight. Keeping the
 * raycast/pull in one deterministic function prevents client/server drift. */
function applyGrapple(k, input, world, dt) {
    const held = (input.buttons & Buttons.Grapple) !== 0;
    if (!held) {
        k.grapOn = false;
        return;
    }
    if (!k.grapOn) {
        const [dx, dy, dz] = yawPitchToDir(input.yaw, input.pitch);
        const ox = k.x, oy = k.y + eyeHeight(k), oz = k.z;
        const ray = { ox, oy, oz, dx, dy, dz };
        // Nearest hookable surface via the collider index (invariant 9). Passing
        // the grapple range as the fallback makes this bit-identical to the old
        // linear `min` scan: nearestStaticHit returns the nearest entry distance
        // capped at `range`, so `best = dist` and `hit ⇔ dist < range` (a box at
        // exactly `range` was excluded by the old `d < best` too). Fired on the
        // grapple LATCH frame — one client-prediction scan + one server scan per
        // hook — which on medieval_castle was a 962-box `rayAabb` sweep.
        const range = MOVE.grapple.range;
        const dist = nearestStaticHit(world, ray, range);
        if (dist < range && dist >= MOVE.grapple.minDist) {
            k.grapOn = true;
            k.grapX = ox + dx * dist;
            k.grapY = oy + dy * dist;
            k.grapZ = oz + dz * dist;
        }
    }
    if (!k.grapOn)
        return;
    const ax = (k.grapX ?? 0) - k.x;
    const ay = (k.grapY ?? 0) - (k.y + eyeHeight(k));
    const az = (k.grapZ ?? 0) - k.z;
    const dist = Math.sqrt(ax * ax + ay * ay + az * az);
    if (dist <= MOVE.grapple.arrive) {
        k.grapOn = false;
        return;
    }
    const speed = MOVE.grapple.pull / dist;
    const blend = 1 - Math.exp(-MOVE.grapple.accel * dt);
    k.vx += (ax * speed - k.vx) * blend;
    k.vy += (ay * speed - k.vy) * blend;
    k.vz += (az * speed - k.vz) * blend;
    k.onGround = false;
}
/**
 * Elytra-style jetpack glide. Deterministic (client + server run it verbatim).
 * Velocity steers toward the look direction; diving (looking down) builds
 * speed, climbing bleeds it; Jump gives thrust; reduced gravity. Landing ends
 * the glide (caller checks onGround). Only invoked when the player has a
 * jetpack and holds the Glide button while airborne.
 */
export function stepGlide(k, input, world) {
    const dt = Math.min(MOVE.maxDtMs, Math.max(MOVE.minDtMs, input.dtMs)) / 1000;
    const g = MOVE.glide;
    // Deploying mid-ride hands control to this function INSTEAD of stepPlayer,
    // which is where the zipline is read and released — so a latched zipIdx just
    // sat there. On landing, glide ends and the next stepPlayer teleported the
    // player back onto the cable at the frozen zipDist. Let go of the cable here
    // (the ride's velocity is already in k.v*, so the launch carries into the
    // glide) and run the same re-grab cooldown stepPlayer would.
    if ((k.zipIdx ?? -1) >= 0) {
        k.zipIdx = -1;
        k.zipCd = MOVE.zip.cooldown;
    }
    else if ((k.zipCd ?? 0) > 0) {
        k.zipCd = Math.max(0, (k.zipCd ?? 0) - dt);
    }
    // stand up only with headroom (mirrors stepPlayer) — force-standing under a
    // low ceiling would embed the player in geometry and freeze them mid-air
    if (k.crouching) {
        k.crouching = false;
        if (collides(world, k, MOVE.standHeight))
            k.crouching = true;
    }
    const [dx, dy, dz] = yawPitchToDir(input.yaw, input.pitch);
    // target airspeed: diving (dy<0) speeds up, climbing (dy>0) slows down
    const dive = -dy; // +1 straight down, −1 straight up
    // afterburner: Sprint held (Shift / L3) scales the target airspeed AND the cap
    const burn = (input.buttons & Buttons.Sprint) !== 0 ? g.sprintMult : 1;
    let target = (g.forward + (dive > 0 ? dive * g.dive : dive * g.climbDrag * 8)) * burn;
    target = Math.max(3, Math.min(g.maxSpeed * burn, target));
    // steer velocity toward the look direction at the target airspeed
    const blend = 1 - Math.exp(-2.6 * dt);
    k.vx += (dx * target - k.vx) * blend;
    k.vy += (dy * target - k.vy) * blend;
    k.vz += (dz * target - k.vz) * blend;
    if ((input.buttons & Buttons.Jump) !== 0)
        k.vy += g.boost * dt; // thrust
    k.vy -= g.gravity * dt; // light gravity
    // Jetpack steering and hook pull combine: flight sets the desired velocity,
    // then the rope bends it toward the anchor. This was previously unreachable
    // because glide bypassed stepPlayer, where grapple processing lived.
    applyGrapple(k, input, world, dt);
    const height = playerHeight(k);
    const slideAxis = (axis, delta) => {
        if (delta === 0)
            return;
        const before = k[axis];
        k[axis] += delta;
        if (collides(world, k, height)) {
            k[axis] = before;
            if (axis === "x")
                k.vx = 0;
            else
                k.vz = 0;
        }
    };
    // SUBSTEPPED integration: afterburner glide moves >2 m per 50 ms input —
    // an endpoint-only check tunnels straight through 0.4 m building walls.
    // Deterministic on client + server (same code, same step count).
    const vmax = Math.max(Math.abs(k.vx), Math.abs(k.vy), Math.abs(k.vz));
    const steps = Math.max(1, Math.ceil((vmax * dt) / 0.6));
    const sdt = dt / steps;
    for (let i = 0; i < steps; i++) {
        slideAxis("x", k.vx * sdt);
        if (k.x < world.minX) {
            k.x = world.minX;
            if (k.vx < 0)
                k.vx = 0;
        }
        if (k.x > world.maxX) {
            k.x = world.maxX;
            if (k.vx > 0)
                k.vx = 0;
        }
        slideAxis("z", k.vz * sdt);
        if (k.z < world.minZ) {
            k.z = world.minZ;
            if (k.vz < 0)
                k.vz = 0;
        }
        if (k.z > world.maxZ) {
            k.z = world.maxZ;
            if (k.vz > 0)
                k.vz = 0;
        }
        const dyv = k.vy * sdt;
        const beforeY = k.y;
        k.y += dyv;
        if (collides(world, k, height)) {
            // settle onto the surface instead of snapping the whole step back
            // (the old full revert "landed" you hovering up to |vy|·dt in the air)
            let lo = 0;
            let hi = dyv;
            for (let j = 0; j < 8; j++) {
                const mid = (lo + hi) / 2;
                k.y = beforeY + mid;
                if (collides(world, k, height))
                    hi = mid;
                else
                    lo = mid;
            }
            k.y = beforeY + lo;
            if (dyv < 0)
                k.onGround = true; // landed → glide ends
            k.vy = 0;
            if (k.onGround)
                break;
        }
        else {
            k.onGround = false;
        }
        // stepPlayer enforces the same authored hard ceiling. Without this clamp,
        // holding jetpack thrust could climb hundreds of metres above the map and
        // releasing Glide teleported the player back to ceilY in one frame.
        if (k.y > world.ceilY) {
            k.y = world.ceilY;
            if (k.vy > 0)
                k.vy = 0;
        }
    }
    // WATER flags: stepGlide bypasses stepPlayer, which is the only other place
    // inWater/submerged are derived — so jetpacking out of the sea kept the
    // underwater tint on (and diving in kept it off) until the glide ended.
    // View-only flags, same thresholds + hysteresis as stepPlayer.
    const wSurf = world.waters.length ? waterSurfaceAt(world, k.x, k.y, k.z, height) : null;
    const eyeY = k.y + eyeHeight(k);
    k.inWater = wSurf !== null && k.y < wSurf - 0.05;
    if (wSurf === null)
        k.submerged = false;
    else if (k.submerged)
        k.submerged = eyeY < wSurf + 0.18;
    else
        k.submerged = eyeY < wSurf - 0.18;
    return k;
}
//# sourceMappingURL=movement.js.map